import {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useMutation, useQuery} from '@apollo/client';
import {
  Alert,
  Badge,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
  Tag,
  Tooltip,
} from 'antd';
import {ArrowLeftOutlined, PlusOutlined} from '@ant-design/icons';
import moment from 'moment';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import NewsModal from '../shared/NewsModal';
import {useExchanges} from 'components/exchangeSelect/ExchangeSelect';
import {makeSelectSubTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';
import {
  actionError,
  loadError,
  notDeployed,
  unionError,
} from '../shared/errors';
import {ActionButton, NO_PERMISSION, TechnicalDetails, Tip} from '../shared/ui';
import FeedEditor from './FeedEditor';
import {SOURCES_SUB_TAB, useRetry} from './NewsSources';
import {
  DELETE_NEWS_SOURCE,
  DETACH_NEWS_SOURCE_COVERAGE,
  GET_INGESTED_ARTICLES,
  GET_NEWS_SOURCE,
  GET_REVIEW_STATS,
  UPDATE_NEWS_SOURCE,
} from './resolvers';
import {
  CONFIRM,
  FEED,
  GNEWS_EDITIONS,
  LIST,
  PAGE,
  SKIP_REASONS,
  STATUS,
  TECH,
  TOAST,
  TRY_AGAIN_TIP,
} from './copy';
import {
  answersFromCoverage,
  fromNow,
  otherExchanges,
  replaceCoverage,
  sourceStatus,
} from './sourceHelpers';
import styles from '../shared/news.module.scss';

const REVIEWS_NEEDED = 20;

const SELECTOR_FIELDS = ['title', 'content', 'image', 'date', 'author'];

const technicalValues = source => ({
  discovery: {
    mode: source.discovery?.mode || 'AUTO',
    feedUrl: source.discovery?.feedUrl,
  },
  useSelectors: source.extraction?.mode === 'SELECTOR',
  selectors: source.extraction?.selectors || {},
  fetch: {
    intervalMinutes: source.fetch?.intervalMinutes,
    maxItemsPerRun: source.fetch?.maxItemsPerRun,
    respectRobots: source.fetch?.respectRobots !== false,
  },
});

const blankToUndefined = object =>
  Object.fromEntries(
    Object.entries(object || {})
      .filter(([key]) => key !== '__typename')
      .map(([key, value]) => [
        key,
        value === '' || value === null ? undefined : value,
      ]),
  );

const editionLabel = value =>
  GNEWS_EDITIONS.find(edition => edition.value === value)?.label || '—';

// What happened to one of the website's latest articles, in words.
const articleOutcome = (article, codeOf) => {
  if (article.exchangeId) return PAGE.usedFor(codeOf(article.exchangeId));
  if (article.status === 'UNROUTED') return PAGE.unplaced;
  if (article.skipReason === 'NO_KEYWORD') {
    return PAGE.skipped(SKIP_REASONS.keyword);
  }
  if (article.skipReason === 'ONE_KEYWORD_IN_TEXT') {
    return PAGE.skipped(SKIP_REASONS.oneKeyword);
  }
  if (article.skipReason === 'OUTSIDE_SECTION') {
    return PAGE.skipped(SKIP_REASONS.section);
  }
  return PAGE.ignored;
};

const NewsSourceDetail = () => {
  const {sourceId} = useParams();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(null);
  const {exchanges} = useExchanges();
  const ops = useSelector(state =>
    makeSelectSubTabOperations(state, NavTabsEnum.News, SOURCES_SUB_TAB),
  );

  const {data, loading, error, refetch} = useQuery(GET_NEWS_SOURCE, {
    variables: {sourceId},
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });
  const articlesQuery = useQuery(GET_INGESTED_ARTICLES, {
    variables: {sourceId, limit: 20},
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const statsQuery = useQuery(GET_REVIEW_STATS, {
    variables: {sourceId},
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const response = data?.newsSource;
  const source =
    response?.__typename === 'RegisteredNewsSource' ? response : null;

  const [update, {loading: saving}] = useMutation(UPDATE_NEWS_SOURCE, {
    onError: actionError('change this website'),
  });
  const [detach] = useMutation(DETACH_NEWS_SOURCE_COVERAGE, {
    onError: actionError('remove this website'),
  });
  const [deleteSource] = useMutation(DELETE_NEWS_SOURCE, {
    onError: actionError('delete this website'),
  });
  const {retry, retrying} = useRetry(refetch);

  const backToList = () => navigate('/dashboard/news/sources');
  const codeOf = id =>
    source?.coverage?.find(row => row.exchangeId === id)?.exchangeCode ||
    exchanges.find(item => item.id === id)?.code ||
    '';

  const change = async (changes, message) => {
    const {data: result} = await update({
      variables: {sourceId, data: changes},
    });
    const payload = result?.updateNewsSource;
    if (!payload) return;
    if (payload.__typename === 'Error') {
      return unionError('change this website', payload);
    }
    toast.success(message);
    refetch();
  };

  if (loading && !source) return <Spin className={styles.spinner} />;

  if (!source) {
    return (
      <div className={styles.root}>
        <Button icon={<ArrowLeftOutlined />} onClick={backToList}>
          {PAGE.back}
        </Button>
        <Alert
          type="error"
          showIcon
          message={loadError('this website', response || error)}
        />
      </div>
    );
  }

  const status = sourceStatus(source);
  const word = STATUS[status.key];
  const paused = source.enabled === false;
  const all = (source.coverage || []).map(row => row.exchangeCode);

  const togglePause = () => {
    const apply = () =>
      change(
        {enabled: paused},
        paused ? TOAST.resumed(source.name) : TOAST.paused(source.name),
      );
    if (paused) return apply();
    Modal.confirm({
      title: CONFIRM.pauseTitle(source.name),
      content: CONFIRM.pause(source.name),
      okText: 'Pause',
      onOk: apply,
    });
  };

  const remove = () =>
    Modal.confirm({
      title: CONFIRM.deleteTitle(source.name),
      content: CONFIRM.delete(source.name, all),
      okText: 'Delete',
      okButtonProps: {danger: true},
      onOk: async () => {
        const {data: result} = await deleteSource({variables: {sourceId}});
        const payload = result?.deleteNewsSource;
        if (!payload) return;
        if (payload.__typename === 'Error') {
          return unionError('delete this website', payload);
        }
        toast.success(TOAST.deleted(source.name));
        backToList();
      },
    });

  const setActive = (row, enabled) => {
    const apply = () =>
      change(
        {coverage: replaceCoverage(source, row.exchangeId, {enabled})},
        enabled
          ? TOAST.on(source.name, row.exchangeCode)
          : TOAST.off(source.name, row.exchangeCode),
      );
    if (enabled) return apply();
    Modal.confirm({
      title: CONFIRM.offTitle(source.name, row.exchangeCode),
      content: CONFIRM.off(source.name, row.exchangeCode),
      okText: 'Turn off',
      onOk: apply,
    });
  };

  const removeFrom = row =>
    Modal.confirm({
      title: CONFIRM.removeTitle(source.name, row.exchangeCode),
      content: CONFIRM.remove(
        source.name,
        row.exchangeCode,
        otherExchanges(source, row.exchangeId),
      ),
      okText: 'Remove',
      okButtonProps: {danger: true},
      onOk: async () => {
        const {data: result} = await detach({
          variables: {sourceId, exchangeId: row.exchangeId},
        });
        const payload = result?.detachNewsSourceCoverage;
        if (!payload) return;
        if (payload.__typename === 'Error') {
          return unionError('remove this website', payload);
        }
        toast.success(TOAST.removed(source.name, row.exchangeCode));
        refetch();
      },
    });

  const statsResponse = statsQuery.data?.newsSourceReviewStats;
  const stats =
    statsResponse?.__typename === 'NewsSourceReviewStatsList'
      ? statsResponse.data
      : null;

  const setAutoPublish = (row, autoPublish) => {
    const apply = () =>
      change(
        {coverage: replaceCoverage(source, row.exchangeId, {autoPublish})},
        TOAST.saved(source.name),
      );
    if (!autoPublish) return apply();
    Modal.confirm({
      title: PAGE.autoPublishConfirmTitle,
      content: PAGE.autoPublishConfirm(source.name, row.exchangeCode),
      okText: 'Continue',
      onOk: apply,
    });
  };

  const autoPublishControl = row => {
    const rowStats = stats?.find(item => item.exchangeId === row.exchangeId);
    const reviewed = rowStats?.reviewed ?? 0;
    // Turning it off is always allowed; turning it on needs the evidence.
    const eligible = reviewed >= REVIEWS_NEEDED || row.autoPublish;
    let evidence = PAGE.notEnough(reviewed);
    if (!stats) evidence = FEED.reviewsNotCounted;
    else if (reviewed >= REVIEWS_NEEDED) {
      evidence = PAGE.evidence(
        rowStats.recentUnchanged,
        rowStats.recentReviewed,
        source.name,
      );
    }
    return (
      <div className={styles.inlineControl}>
        <Tooltip title={ops?.update ? PAGE.autoPublishTip : NO_PERMISSION}>
          <Switch
            size="small"
            checked={Boolean(row.autoPublish)}
            disabled={!ops?.update || !eligible}
            onChange={checked => setAutoPublish(row, checked)}
          />
        </Tooltip>
        <Tooltip title={PAGE.evidenceTip}>
          <span className={styles.muted}>
            {row.autoPublish ? PAGE.active : evidence}
          </span>
        </Tooltip>
      </div>
    );
  };

  // What the exchange takes from this website: every article, the ones that
  // mention its words (the first few shown, the rest counted), or one section.
  const usesValue = answers => {
    if (answers.cover === 'market') return FEED.usesAll;
    if (answers.cover === 'section') {
      return FEED.usesSection(answers.section);
    }
    const shown = answers.keywords.slice(0, 6);
    const rest = answers.keywords.slice(6);
    return (
      <>
        <span>{FEED.usesKeywords}</span>
        <span className={styles.keywordChips}>
          {shown.map(word => (
            <Tag key={word}>{word}</Tag>
          ))}
          {rest.length ? (
            <Tooltip title={rest.join(', ')}>
              <Tag className={styles.moreTag}>
                {FEED.moreWords(rest.length)}
              </Tag>
            </Tooltip>
          ) : null}
        </span>
      </>
    );
  };

  const saveTechnical = values =>
    change(
      {
        discovery: blankToUndefined(values.discovery),
        extraction: {
          mode: values.useSelectors ? 'SELECTOR' : 'AUTO',
          selectors: blankToUndefined(values.selectors),
        },
        fetch: blankToUndefined(values.fetch),
      },
      TOAST.saved(source.name),
    );

  const articlesResponse = articlesQuery.data?.ingestedArticles;
  const articles =
    articlesResponse?.__typename === 'IngestedArticleList'
      ? articlesResponse.data
      : [];
  const articlesError =
    articlesResponse?.__typename === 'Error'
      ? articlesResponse
      : articlesQuery.error;

  return (
    <div className={styles.root}>
      <Button icon={<ArrowLeftOutlined />} onClick={backToList}>
        {PAGE.back}
      </Button>

      <div className={styles.toolbar}>
        <div>
          <h2 className={styles.title}>{source.name}</h2>
          <Tip
            label={
              <span>
                <Badge status={word.badge} text={word.label} />
                {' · '}
                {PAGE.line(
                  fromNow(source.health?.lastSuccessAt) ||
                    LIST.noneYet.toLowerCase(),
                  source.health?.itemsLast7d ?? 0,
                )}
              </span>
            }
            tip={PAGE.statusTip}
          />
        </div>
        <div className={styles.cardActions}>
          <ActionButton
            allowed={ops?.update}
            tip={paused ? PAGE.resumeTip : PAGE.pauseTip}
            onClick={togglePause}>
            {paused ? PAGE.resume : PAGE.pause}
          </ActionButton>
          <ActionButton
            danger
            allowed={ops?.delete}
            tip={LIST.deleteTip}
            onClick={remove}>
            {LIST.delete}
          </ActionButton>
        </div>
      </div>

      {status.message &&
      ['trouble', 'paused', 'waiting'].includes(status.key) ? (
        <div className={styles.problem}>
          <span>{status.message}</span>
          {status.action ? (
            <ActionButton
              allowed={ops?.update}
              tip={TRY_AGAIN_TIP}
              loading={retrying === source.id}
              onClick={() => retry(source)}>
              {status.action}
            </ActionButton>
          ) : null}
        </div>
      ) : null}

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h3>
            <Tip label={PAGE.feeds} tip={PAGE.feedsTip} />
          </h3>
          <ActionButton
            icon={<PlusOutlined />}
            allowed={ops?.update}
            tip={PAGE.feedAnotherTip}
            onClick={() => setEditing({row: null})}>
            {PAGE.feedAnother}
          </ActionButton>
        </div>
        <div className={styles.feedList}>
          {(source.coverage || []).map(row => {
            const active = row.enabled !== false;
            const answers = answersFromCoverage(row);
            return (
              <div key={row.exchangeId} className={styles.feedCard}>
                <div className={styles.feedHead}>
                  <span className={styles.exchangeBadge}>
                    {row.exchangeCode || row.exchangeName}
                  </span>
                  <Badge
                    status={active ? 'success' : 'default'}
                    text={active ? PAGE.active : PAGE.inactive}
                  />
                  <span className={styles.feedActions}>
                    <Tooltip
                      title={
                        ops?.update
                          ? LIST.activeOnTip(row.exchangeCode)
                          : NO_PERMISSION
                      }>
                      <span className={styles.inlineControl}>
                        <span className={styles.muted}>
                          {LIST.activeOn(row.exchangeCode)}
                        </span>
                        <Switch
                          size="small"
                          checked={active}
                          disabled={!ops?.update}
                          onChange={checked => setActive(row, checked)}
                        />
                      </span>
                    </Tooltip>
                    <ActionButton
                      size="small"
                      allowed={ops?.update}
                      tip={PAGE.editTip}
                      onClick={() => setEditing({row})}>
                      {PAGE.edit}
                    </ActionButton>
                    {source.coverage.length > 1 ? (
                      <ActionButton
                        size="small"
                        danger
                        allowed={ops?.update}
                        tip={LIST.removeFromTip(row.exchangeCode)}
                        onClick={() => removeFrom(row)}>
                        {LIST.removeFrom(row.exchangeCode)}
                      </ActionButton>
                    ) : null}
                  </span>
                </div>
                <dl className={styles.facts}>
                  <dt>
                    <Tip label={FEED.uses} tip={FEED.usesTip} />
                  </dt>
                  <dd>{usesValue(answers)}</dd>
                  <dt>
                    <Tip label={FEED.reliability} tip={FEED.reliabilityTip} />
                  </dt>
                  <dd>{FEED.reliabilityLabel[answers.reliability]}</dd>
                  <dt>
                    <Tip label={PAGE.autoPublish} tip={PAGE.autoPublishTip} />
                  </dt>
                  <dd>{autoPublishControl(row)}</dd>
                </dl>
              </div>
            );
          })}
        </div>
      </div>

      <div className={styles.panel}>
        <h3>
          <Tip label={PAGE.latest} tip={PAGE.latestTip} />
        </h3>
        {articlesError ? (
          <Alert
            type={notDeployed(articlesError.message) ? 'info' : 'error'}
            showIcon
            message={loadError("this website's latest articles", articlesError)}
          />
        ) : articles.length ? (
          <ul className={styles.articles}>
            {articles.map(article => (
              <li key={article.id}>
                <a href={article.canonicalUrl} target="_blank" rel="noreferrer">
                  {article.title || article.canonicalUrl}
                </a>
                <Tooltip title={PAGE.usedTip}>
                  <span
                    className={
                      article.exchangeId ? styles.used : styles.skipped
                    }>
                    {moment(
                      article.publishedDate || article.fetchedAt,
                    ).fromNow()}
                    {' · '}
                    {articleOutcome(article, codeOf)}
                  </span>
                </Tooltip>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.muted}>{PAGE.latestNone}</p>
        )}

        <TechnicalDetails>
          <Form
            key={source.updatedAt}
            initialValues={technicalValues(source)}
            layout="vertical"
            onFinish={saveTechnical}
            disabled={!ops?.update}>
            <div className={styles.formGrid}>
              <Form.Item
                name={['discovery', 'mode']}
                label={TECH.howWeRead}
                tooltip={TECH.howWeReadTip}>
                <Select
                  options={Object.entries(TECH.modes).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name={['discovery', 'feedUrl']}
                label={TECH.feedAddress}
                tooltip={TECH.feedAddressTip}>
                <Input />
              </Form.Item>
              <Form.Item
                name={['fetch', 'intervalMinutes']}
                label={TECH.interval}
                tooltip={TECH.intervalTip}>
                <InputNumber min={1} precision={0} />
              </Form.Item>
              <Form.Item
                name={['fetch', 'maxItemsPerRun']}
                label={TECH.perRead}
                tooltip={TECH.perReadTip}>
                <InputNumber min={1} precision={0} />
              </Form.Item>
              <Form.Item
                name={['fetch', 'respectRobots']}
                label={TECH.robots}
                tooltip={TECH.robotsTip}
                valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item
                name="useSelectors"
                label={TECH.selectors}
                tooltip={TECH.selectorsTip}
                valuePropName="checked">
                <Switch />
              </Form.Item>
            </div>
            {/* The selectors only matter once Page selectors is on. Hidden,
                not unmounted, so saving keeps what was typed. */}
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) =>
                prev.useSelectors !== next.useSelectors
              }>
              {({getFieldValue}) => {
                const on = Boolean(getFieldValue('useSelectors'));
                return (
                  <>
                    {on ? (
                      <p className={styles.reason}>{TECH.selectorsHelp}</p>
                    ) : null}
                    <div className={styles.formGrid}>
                      {SELECTOR_FIELDS.map(field => (
                        <Form.Item
                          key={field}
                          hidden={!on}
                          name={['selectors', field]}
                          label={TECH.selectorFields[field].label}
                          tooltip={TECH.selectorFields[field].tip}>
                          <Input
                            placeholder={TECH.selectorFields[field].placeholder}
                          />
                        </Form.Item>
                      ))}
                    </div>
                  </>
                );
              }}
            </Form.Item>
            <p className={styles.muted}>
              <Tip label={TECH.gnews} tip={TECH.gnewsTip} />:{' '}
              {(source.coverage || [])
                .map(
                  row => `${row.exchangeCode} ${editionLabel(row.gnewsLocale)}`,
                )
                .join(' · ')}
            </p>
            <div className={styles.switchList}>
              {(source.coverage || []).map(row => (
                <span key={row.exchangeId} className={styles.inlineControl}>
                  <Switch
                    size="small"
                    checked={Boolean(row.autoPublish)}
                    disabled={!ops?.update}
                    onChange={checked => setAutoPublish(row, checked)}
                  />
                  <Tip
                    label={TECH.autoPublish(row.exchangeCode)}
                    tip={TECH.autoPublishTip}
                  />
                </span>
              ))}
            </div>
            <p className={styles.muted}>
              <Tip label={TECH.lastError} tip={TECH.lastErrorTip} />:{' '}
              {source.health?.lastError || '—'}
            </p>
            <div className={styles.modalActions}>
              <ActionButton
                type="primary"
                htmlType="submit"
                allowed={ops?.update}
                tip={TECH.saveTip}
                loading={saving}>
                {TECH.save}
              </ActionButton>
            </div>
          </Form>
        </TechnicalDetails>
      </div>

      <NewsModal
        isOpen={Boolean(editing)}
        onClose={() => setEditing(null)}
        width={720}>
        {editing ? (
          <FeedEditor
            source={source}
            row={editing.row}
            exchanges={exchanges}
            canUpdate={ops?.update}
            onSaved={() => {
              setEditing(null);
              refetch();
            }}
          />
        ) : null}
      </NewsModal>
    </div>
  );
};

export default NewsSourceDetail;
